import { useState, useEffect } from 'react'
import { Users } from 'lucide-react'
import api, { Organisation, TargetDto } from '../../services/api'
import Button from '../common/Button'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyState from '../common/EmptyState'
import AddTargetModal from './AddTargetModal'
import CsvImportModal from './CsvImportModal'
import DeleteConfirmModal from './DeleteConfirmModal'

interface TargetPoolManagerProps {
  organisation: Organisation
  onUpdate?: () => void
}

const TargetPoolManager: React.FC<TargetPoolManagerProps> = ({ organisation, onUpdate }) => {
  const [targets, setTargets] = useState<TargetDto[]>([])
  const [departments, setDepartments] = useState<string[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [targetToDelete, setTargetToDelete] = useState<TargetDto | null>(null)
  const [selectedTargets, setSelectedTargets] = useState<Set<string>>(new Set())

  const orgId = organisation.id || organisation.organisation_id || ''

  useEffect(() => {
    loadTargets()
  }, [orgId])

  const loadTargets = async () => {
    if (!orgId) return
    
    try {
      setLoading(true)
      setError(null)
      const response = await api.targetPool.list(orgId)
      setTargets(response.targets)
      setDepartments(response.departments)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load targets')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      await api.targetPool.exportCsv(orgId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export CSV')
    }
  }

  const handleDelete = async (target: TargetDto) => {
    setTargetToDelete(target)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!targetToDelete) return
    
    try {
      await api.targetPool.remove(orgId, targetToDelete.id)
      await loadTargets()
      setShowDeleteModal(false)
      setTargetToDelete(null)
      onUpdate?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete target')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedTargets.size === 0) return
    
    try {
      await api.targetPool.removeBatch(orgId, Array.from(selectedTargets))
      await loadTargets()
      setSelectedTargets(new Set())
      onUpdate?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete targets')
    }
  }

  const handleClearAll = async () => {
    if (!confirm('Are you sure you want to remove all targets? This cannot be undone.')) {
      return
    }
    
    try {
      await api.targetPool.clear(orgId)
      await loadTargets()
      onUpdate?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear target pool')
    }
  }

  const toggleSelection = (targetId: string) => {
    const newSelection = new Set(selectedTargets)
    if (newSelection.has(targetId)) {
      newSelection.delete(targetId)
    } else {
      newSelection.add(targetId)
    }
    setSelectedTargets(newSelection)
  }

  const toggleSelectAll = () => {
    if (selectedTargets.size === filteredTargets.length) {
      setSelectedTargets(new Set())
    } else {
      setSelectedTargets(new Set(filteredTargets.map(t => t.id)))
    }
  }

  const filteredTargets = selectedDepartment === 'all' 
    ? targets 
    : targets.filter(t => t.department === selectedDepartment)

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Target Pool</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {targets.length} target{targets.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleExport} disabled={targets.length === 0}>
            Export CSV
          </Button>
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            Import CSV
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            Add Target
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {departments.length > 0 && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filter by department:
          </label>
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">All ({targets.length})</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>
                {dept} ({targets.filter(t => t.department === dept).length})
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedTargets.size > 0 && (
        <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            {selectedTargets.size} target{selectedTargets.size !== 1 ? 's' : ''} selected
          </p>
          <Button variant="danger" size="sm" onClick={handleBulkDelete}>
            Delete Selected
          </Button>
        </div>
      )}

      {targets.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No targets yet"
          description="Add email addresses to start creating phishing campaigns"
          action={{
            label: "Add Target",
            onClick: () => setShowAddModal(true)
          }}
        />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedTargets.size === filteredTargets.length && filteredTargets.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Job Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTargets.map((target) => (
                <tr key={target.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedTargets.has(target.id)}
                      onChange={() => toggleSelection(target.id)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {target.target_email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {(target.given_name || target.surname) ? `${target.given_name || ''} ${target.surname || ''}`.trim() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {target.job_title || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {target.department || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => handleDelete(target)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {targets.length > 0 && (
        <div className="flex justify-end">
          <Button variant="danger" size="sm" onClick={handleClearAll}>
            Clear All Targets
          </Button>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddTargetModal
          organisationId={orgId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            loadTargets()
            setShowAddModal(false)
            onUpdate?.()
          }}
        />
      )}

      {showImportModal && (
        <CsvImportModal
          organisationId={orgId}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            loadTargets()
            setShowImportModal(false)
            onUpdate?.()
          }}
        />
      )}

      {showDeleteModal && targetToDelete && (
        <DeleteConfirmModal
          targetEmail={targetToDelete.target_email}
          onClose={() => {
            setShowDeleteModal(false)
            setTargetToDelete(null)
          }}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  )
}

export default TargetPoolManager
